import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerWithFamily, checkBetaCode, initializeAuth } from '../services/apiService';
import { supabase } from '../services/supabaseClient';
import { storageService } from '../services/storageService';
import { useAppContext } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { supported_languages } from '../i18n/translations';
import { i18n } from '../i18n/i18n';
import { isNative, oauthRedirectTo, openSignInPage } from '../platform/native';
import {
  authCardClass,
  authErrorBannerClass,
  authInputBaseClass,
  authInputBorderErr,
  authInputBorderOk,
  authInputClass,
  authLabelClass,
  authScreenShellClass,
} from './authUiClasses';

const Signup: React.FC = () => {
  const [betaCode, setBetaCode] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [pwdTouched, setPwdTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [family, setFamily] = useState('');
  const [agree, setAgree] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const navigate = useNavigate();
  const { refreshUserData, refreshFamilyData, bindDevice } = useAppContext() as any;

  useEffect(() => {
    if (!name) return;
    if (usernameEdited) return;
    const slug = name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_')
      .slice(0, 20);
    setUsername(slug);
  }, [name, usernameEdited]);

  const validPassword = (pwd: string): boolean => {
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasDigit = /\d/.test(pwd);
    return typeof pwd === 'string' && pwd.length >= 12 && hasUpper && hasLower && hasDigit;
  };

  const evalPassword = (pwd: string) => {
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasDigit = /\d/.test(pwd);
    const lengthOk = pwd.length >= 12;
    const classesOk = hasUpper && hasLower && hasDigit;
    return { lengthOk, hasUpper, hasLower, hasDigit, classesOk };
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!betaCode.trim()) {
      setErr(i18n.t('auth.err_code_required'));
      return;
    }
    if (!agree) {
      setErr(i18n.t('auth.err_agree'));
      return;
    }
    if (!name.trim() || !email.trim() || !family.trim()) {
      setErr(i18n.t('auth.err_fields'));
      return;
    }
    if (!validPassword(password)) {
      setErr(i18n.t('auth.password_policy_msg'));
      return;
    }
    if (password !== confirm) {
      setErr(i18n.t('auth.password_mismatch'));
      return;
    }
    setIsLoading(true);
    try {
      // Checked before signUp() so a bad code never creates an orphaned auth account.
      const codeValid = await checkBetaCode(betaCode.trim());
      if (!codeValid) {
        setErr(i18n.t('auth.err_code_invalid'));
        setIsLoading(false);
        return;
      }
      const { token } = await registerWithFamily(name.trim(), email.trim(), password, family.trim(), username.trim() || undefined, betaCode.trim());
      storageService.setAuthToken(token);
      initializeAuth(token);
      await refreshUserData();
      await refreshFamilyData();
      // Ensure this device is bound so MemberChooser is available on switch
      try { await bindDevice('Web Browser'); } catch {}
      navigate('/onboarding', { replace: true });
    } catch (e: any) {
      setErr(e?.message || i18n.t('auth.err_signup_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSignup = async (provider: 'google' | 'facebook' | 'apple') => {
    // In the iOS/Android apps the sign-in page opens in the system browser and comes back to the app.
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: oauthRedirectTo(), skipBrowserRedirect: isNative },
    });
    if (error) setErr(error.message);
    else if (isNative && data?.url) await openSignInPage(data.url);
  };

  return (
    <div className={authScreenShellClass}>
      <div className={authCardClass}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-gray-800 dark:text-gray-100 text-2xl font-semibold">{i18n.t('auth.signup_title')}</h2>
          <LanguageSelect />
        </div>
        {err && <div className={authErrorBannerClass}>{err}</div>}
        <form onSubmit={onSubmit}>
          <div className="mb-4">
            <label className={authLabelClass}>{i18n.t('auth.beta_code')}</label>
            <input
              className={authInputClass}
              value={betaCode}
              onChange={(e) => setBetaCode(e.target.value)}
              placeholder={i18n.t('auth.beta_code_ph')}
            />
          </div>
          <div className="mb-4">
            <label className={authLabelClass}>{i18n.t('auth.full_name')}</label>
            <input className={authInputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder={i18n.t('auth.full_name_ph')} />
          </div>
          <div className="mb-4">
            <label className={authLabelClass}>{i18n.t('auth.username')}</label>
            <input
              className={authInputClass}
              value={username}
              onChange={(e) => {
                setUsernameEdited(true);
                setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''));
              }}
              placeholder="jane_doe"
            />
          </div>
          <div className="mb-4">
            <label className={authLabelClass}>{i18n.t('auth.email_label')}</label>
            <input type="email" className={authInputClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" />
          </div>
          <div className="mb-4">
            <label className={authLabelClass}>{i18n.t('login.password_label')}</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                className={`${authInputBaseClass} pr-10 ${pwdTouched && !validPassword(password) ? authInputBorderErr : authInputBorderOk}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setPwdTouched(true)}
                placeholder="••••••••••••"
                aria-invalid={pwdTouched && !validPassword(password)}
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} aria-label={i18n.t(showPwd ? 'auth.hide_password' : 'auth.show_password')} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 dark:text-gray-400">
                {showPwd ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-8 1.02-2.76 2.98-5 5.38-6.35M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88M6.1 6.1 17.9 17.9"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            <div className="text-xs mt-2 space-y-1" aria-live="polite">
              {(() => {
                const r = evalPassword(password);
                const item = (ok: boolean, text: string) => (
                  <div className={ok ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}>• {text}</div>
                );
                return (
                  <>
                    {item(r.lengthOk, i18n.t('auth.rule_length'))}
                    {item(r.hasUpper, i18n.t('auth.rule_upper'))}
                    {item(r.hasLower, i18n.t('auth.rule_lower'))}
                    {item(r.hasDigit, i18n.t('auth.rule_digit'))}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="mb-4">
            <label className={authLabelClass}>{i18n.t('auth.confirm_password_label')}</label>
            <div className="relative">
              <input
                type={showConfirmPwd ? 'text' : 'password'}
                className={`${authInputBaseClass} pr-10 ${confirmTouched && password !== confirm ? authInputBorderErr : authInputBorderOk}`}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onBlur={() => setConfirmTouched(true)}
                placeholder="••••••••••••"
                aria-invalid={confirmTouched && password !== confirm}
              />
              <button type="button" onClick={() => setShowConfirmPwd(!showConfirmPwd)} aria-label={i18n.t(showConfirmPwd ? 'auth.hide_password' : 'auth.show_password')} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 dark:text-gray-400">
                {showConfirmPwd ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-8 1.02-2.76 2.98-5 5.38-6.35M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88M6.1 6.1 17.9 17.9"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {confirmTouched || confirm ? (
              <div
                className={`text-xs mt-2 ${password === confirm ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}
                aria-live="polite"
              >
                {password === confirm ? i18n.t('auth.password_match') : i18n.t('auth.password_mismatch')}
              </div>
            ) : null}
          </div>
          <div className="mb-4">
            <label className={authLabelClass}>{i18n.t('auth.family_name')}</label>
            <input className={authInputClass} value={family} onChange={(e) => setFamily(e.target.value)} placeholder={i18n.t('auth.family_name_ph')} />
          </div>
          <label className="flex items-center gap-2 mb-4 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="rounded border-gray-300 text-indigo-600 dark:border-gray-600 dark:bg-gray-900" />
            <span>
              {/* The public website's legal pages (antnest.app; until it is online these links don't load). */}
              {i18n.t('auth.agree_before')}{' '}
              <a href="https://antnest.app/terms.html" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 underline">
                {i18n.t('auth.terms')}
              </a>{' '}
              {i18n.t('auth.and')}{' '}
              <a href="https://antnest.app/privacy.html" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 underline">
                {i18n.t('auth.privacy')}
              </a>
            </span>
          </label>
          <button type="submit" disabled={isLoading} className="w-full h-12 py-0 flex items-center justify-center bg-btn-primary hover:bg-btn-primary-hover text-btn-text-primary border-none rounded-lg text-base font-semibold disabled:opacity-70">
            {isLoading ? i18n.t('auth.creating_account') : i18n.t('auth.create_account')}
          </button>
        </form>
        <div className="my-4 flex items-center">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
          <span className="px-3 text-gray-500 dark:text-gray-400 text-sm">{i18n.t('auth.or')}</span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
        </div>
        <div className="space-y-2 flex flex-col items-center">
          <button
            type="button"
            onClick={() => handleOAuthSignup('google')}
            className="w-full max-w-[320px] mx-auto inline-flex items-center justify-center gap-2 h-12 px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
          >
            <img src="/google_g.webp" alt="" aria-hidden="true" className="w-5 h-5" />
            <span>{i18n.t('auth.signup_google')}</span>
          </button>
          <button
            type="button"
            onClick={() => handleOAuthSignup('facebook')}
            className="w-full max-w-[320px] mx-auto inline-flex items-center justify-center gap-2 h-12 px-4 rounded-lg bg-[#1877F2] text-white"
          >
            <img src="/Facebook_Logo.png" alt="" aria-hidden="true" className="w-5 h-5" />
            <span>{i18n.t('auth.signup_facebook')}</span>
          </button>
          <button
            type="button"
            onClick={() => handleOAuthSignup('apple')}
            className="w-full max-w-[320px] mx-auto inline-flex items-center justify-center gap-2 h-12 px-4 rounded-lg bg-black text-white dark:border dark:border-gray-500"
          >
            <img src="/Apple_Logo.png" alt="" aria-hidden="true" className="w-5 h-5 invert" />
            <span>{i18n.t('auth.signup_apple')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Signup;

const LanguageSelect: React.FC = () => {
  const { current_language, set_language } = useLanguage();
  return (
    <select
      className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
      value={current_language}
      onChange={(e) => set_language(e.target.value as any)}
      aria-label={i18n.t('auth.choose_language')}
    >
      {supported_languages.map((l) => (
        <option key={l.code} value={l.code}>{l.label}</option>
      ))}
    </select>
  );
};


